import routes from 'configs/routes';
import { createBrowserHistory } from 'history';
import { DEFAULT_SELECTED_PRODUCT } from 'configs/products';
import Track from 'common/DataObjects/Track';
import { trimBoth } from 'utils/formatters';
import { getLS, removeLS } from 'features/PersistentStorage/localStor';
import { ModalsByURLSearchParams } from 'configs/modals';

const BASE_PATH = '/heste';

export const history = createBrowserHistory({ basename: BASE_PATH });

export const getUrl = (url = '') => {
    if (url && url.startsWith('http')) {
        return url;
    }

    if (url && url.startsWith(BASE_PATH)) {
        // url already contains "BASE_PATH". For example, it's a link from local storage that already created with getUrl
        return url;
    }

    return `${BASE_PATH}/${url.startsWith('/') ? url.substr(1) : url}`;
};

export const getHost = () => `${window.location.host}${BASE_PATH}`;
export const getOrigin = () => `${window.location.origin}${BASE_PATH}`;

export const navigateWithRefresh = (url) => {
    window.location.href = getUrl(url);
};

//@TODO Get locale dynamically
//@TODO Get [current] property to be working correctly. Spec: fakeLocationPathname('/users/1/posts/3');     expect(satisfiesRoute({ path: '/news/' })).toEqual(false);
const lang = () => 'DK';

export const satisfiesRoute = (route) => {
    const multiPathPattern = '[0-9A-z-_æøåÆØÅ]*';
    const pattern = route.path.replace(/:\w+/gi, multiPathPattern);
    return new RegExp(pattern).test(history.location.pathname);
};

export const findRoute = (routeId) => getRoutes().find((r) => r.id === routeId);

export const getRoutes = () => {
    return routes.map((r) => wrapRoute(r));
};

export const wrapRoute = (r) => {
    const path = typeof r.path === 'object' ? r.path[lang()] : r.path;
    return {
        id: r.id,
        path,
        current: satisfiesRoute({ path }),
        component:
            typeof r.component === 'object' ? r.component[lang()] : r.component,
    };
};

export const isNewsItemPage = () =>
    /\/25-nyt\/[0-9A-z-_æøåÆØÅ]*\/?/.test(
        decodeURIComponent(history.location.pathname)
    );

export const isHomePage = () => {
    return (
        history.location.pathname === '/hjem' ||
        history.location.pathname.startsWith(`${EVENT_PATH[lang()]}/`) ||
        history.location.pathname.startsWith(`/${EVENT_PATH[lang()]}/`) ||
        history.location.pathname === '/'
    );
};

export const EVENT_PATH = {
    DK: 'spil',
    EN: 'event',
};

export const createEventLink = ({ raceDay, product, raceIndex = 0 }) => {
    if (!raceDay || !product || !raceDay.track.name) {
        return '';
    }
    let params = `/${EVENT_PATH[lang()]}/${raceDay.date}`;
    params = `${params}/${product.name ||
        DEFAULT_SELECTED_PRODUCT.name}/${Track.getSlug(
        raceDay.track.name || ''
    )}`;
    return `${params}/${raceIndex + 1}`;
};

export const addQueryParam = (url, key, value) => {
    url = new URL(url);
    url.searchParams.append(key, value);
    return decodeURIComponent(
        url.origin + url.pathname + '?' + url.searchParams.toString()
    );
};
/**
 * Makes array of values from object for the path described in string.
 * Object like { param1: 'foo', param2: 'bar', param3: undefined }
 * using pathname schema like '/:param1/:param2/:param3'
 * would be transformed to the following:
 * /foo/bar/:param3
 *
 * @param {Object}        o
 * @param {String}        pathnameSchema
 * @return {any[]}
 */
export const objectToPathname = (o, pathnameSchema) => {
    return (
        pathnameSchema
            .split('/')
            // .filter((s) => o.hasOwnProperty(s.substr(1)))
            .map((s) => o[s.substr(1)] || s)
    );
};

export const pathnameToObject = (pathname, pathnameSchema) => {
    const keys = trimBoth(pathnameSchema, '/').split('/');
    const values = trimBoth(pathname, '/').split('/');
    const entries = keys.map((key, index) => [key.slice(1), values[index]]);
    return Object.fromEntries(entries);
};

export const removeSegment = (pathname, segment) => {
    return trimBoth(
        pathname
            .split('/')
            .filter((s) => s !== trimBoth(segment, '/'))
            .join('/'),
        '/'
    );
};

export const queryStringToObject = (queryString) =>
    Object.fromEntries(Array.from(new URLSearchParams(queryString)));

/**
 * Determines if one of the parameters exist in URL query
 * Optional validator argument is a function checking
 * the value of the URL parameters (will be called for each of them)
 * @param {string[]} parameters
 * @param {function.<boolean>} validator
 * @return {boolean}
 */
export const hasURLParameters = (parameters, validator = () => true) => {
    const url = new URL(window.location.href);

    for (let [key, value] of url.searchParams.entries()) {
        if (parameters.includes(key) && validator(key, value)) {
            return true;
        }
    }

    return false;
};

export const getURLParameter = (paramName) => {
    //@TODO Sanitize URL parameter
    const url = new URL(window.location.href);
    return url.searchParams.get(paramName) || '';
};

export const checkURLs = (URLs) => {
    return URLs.some(checkURL);
};

export const checkURL = (urlPart) => {
    return history.location.pathname.includes(urlPart);
};

export const navigateToRaceDay = (date, productId, trackName) => {
    const path = `/spil/${date}/${productId}/${Track.getSlug(trackName)}/1`;
    history.push(path);
};

export const redirectByLocalStorageParameter = (parameterName) => {
    const redirectFromLS = getLS(parameterName);

    if (redirectFromLS) {
        removeLS(parameterName);
        history.location.pathname = redirectFromLS;
    }
};

export const isPromoPage = () => {
    const pathname = history.location.pathname;
    const segments = pathname.split('/');
    return segments[1] && segments[1] === 'promo';
};

function trimSlashes(str) {
    return str.replace(/^\/|\/$/g, '');
}

export const isOnPath = (path) => {
    return trimSlashes(history.location.pathname) === trimSlashes(path);
};

export const extractModalIdQueryParam = (modalId) => {
    const [modalQueryParam] =
        Object.entries(ModalsByURLSearchParams)
            .map(([queryParam, { modalId }]) => [queryParam, modalId])
            .find(([, configModalId]) => configModalId === modalId) || [];

    return modalQueryParam;
};

export const wasModalOpenByQueryParam = (
    modalId,
    queryString = history.location.search
) => {
    const modalQueryParam = extractModalIdQueryParam(modalId);

    if (!modalQueryParam) {
        return false;
    }

    const queryParams = queryStringToObject(queryString);

    const hasModalIdInQueryString = queryParams.hasOwnProperty(modalQueryParam);

    const value = queryParams[modalQueryParam];

    return hasModalIdInQueryString && value === '1';
};

export const removeQueryParamFromBrowserHistory = (param) => {
    const queryParams = new URLSearchParams(history.location.search);

    if (queryParams.has(param)) {
        queryParams.delete(param);
        history.replace({
            search: queryParams.toString(),
        });
    }
};

export const syncModalStateWithQueryParams = (
    modalId,
    queryString = history.location.search
) => {
    if (wasModalOpenByQueryParam(modalId, queryString)) {
        const queryParam = extractModalIdQueryParam(modalId);
        removeQueryParamFromBrowserHistory(queryParam);
    }
};
